import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import dashboardApi from '../../middleware/api/Dashboard/dashboardApi'
import { LoadingState, ValidationError } from '../../types/commonTypes'
import { ApiLoadingState } from '../../middleware/api/apiList'
import { RootState } from '../../stateStore'
import {
  ScheduleInterviewSliceState,
  ScheduleInterviewApiProps,
  ScheduleInterviewDetails,
  ScheduleInterviewResponse,
} from '../../types/Dashboard/ScheduledInterviews/ScheduleInterviewsType'

const getAllPendingInterviewComments = createAsyncThunk(
  'jobapplicant/getAllPendingInterviewComments',
  async (props: ScheduleInterviewApiProps, thunkApi) => {
    try {
      return await dashboardApi.getAllPendingInterviewComments(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialInterviewCommentsState: ScheduleInterviewSliceState = {
  InterviewComment: [],
  listSize: 0,
  isLoading: ApiLoadingState.idle,
  error: null,
}

const scheduleInterviewListSlice = createSlice({
  name: 'scheduleInterviewComments',
  initialState: initialInterviewCommentsState,
  reducers: {
    clearEmployeeList: (state) => {
      state.InterviewComment = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllPendingInterviewComments.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getAllPendingInterviewComments.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        // Some environments may return the array directly instead of an object with { list, size }.
        // Handle both shapes gracefully.
        const payload = action.payload as unknown
        if (Array.isArray(payload)) {
          state.InterviewComment = payload as ScheduleInterviewDetails[]
          state.listSize = payload.length
        } else {
          const obj = payload as Partial<ScheduleInterviewResponse> & {
            list?: ScheduleInterviewDetails[]
            size?: number
          }
          state.InterviewComment = obj.list ?? []
          state.listSize = obj.size ?? (obj.list ? obj.list.length : 0)
        }
      })
      .addCase(getAllPendingInterviewComments.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
        state.InterviewComment = []
        state.listSize = 0
      })
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.scheduleInterviewComments.isLoading
const scheduleInterview = (state: RootState): ScheduleInterviewDetails[] =>
  state.scheduleInterviewComments.InterviewComment
const listSize = (state: RootState): number =>
  state.scheduleInterviewComments.listSize

const PendingInterviewCommentsThunk = {
  getAllPendingInterviewComments,
}

const PendingInterviewCommentsSelectors = {
  isLoading,
  scheduleInterview,
  listSize,
}

export const PendingInterviewCommentsService = {
  ...PendingInterviewCommentsThunk,
  actions: scheduleInterviewListSlice.actions,
  selectors: PendingInterviewCommentsSelectors,
}

export default scheduleInterviewListSlice.reducer
